// frontend/src/pages/admin/GeneralSettings.js
import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Select, Button, Spin, Card, Typography, Space, Upload, message } from 'antd';
import { SaveOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';
import './users.css';

const { Title } = Typography;
const { Option } = Select;

const SETTING_KEYS = {
  APP_TITLE: 'appTitle',
  DATE_FORMAT: 'dateFormat',
  TIME_FORMAT: 'timeFormat',
  LOGO_URL: 'companyLogoUrl',
  COMPANY_NAME: 'companyName',
  LOGIN_TITLE: 'loginPageTitle'
};

const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '25/06/2025' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '06/25/2025' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2025-06-25' }
];

const TIME_FORMATS = [
  { value: 'hh:mm A', label: '12-hour', example: '02:30 PM' },
  { value: 'HH:mm', label: '24-hour', example: '14:30' }
];

const GeneralSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialDataLoading, setInitialDataLoading] = useState(true);
  const notificationApi = useNotificationApi();

  const showError = useCallback((title, content, key = 'globalError', duration = 4.5) => {
    notificationApi.destroy(key);
    notificationApi.error({
      key,
      message: title,
      description: content,
      duration,
      placement: 'topRight',
      style: { width: 400, zIndex: 10000 }
    });
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.destroy(key);
    notificationApi.success({
      key,
      message: title,
      description,
      duration: 3,
      placement: 'topRight',
      style: { width: 400, zIndex: 10000 }
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    const notification = notificationApi.open({
      key,
      message: messageContent,
      duration: 0,
      placement: 'topRight',
      style: { width: 400, zIndex: 10000 }
    });

    return () => {
      if (typeof notification === 'function') {
        notification();
      } else {
        notificationApi.destroy(key);
      }
    };
  }, [notificationApi]);

  const fetchSettings = useCallback(async () => {
    setInitialDataLoading(true);
    const hideLoading = showLoading('fetchSettingsLoading', 'Loading settings...');

    try {
      const response = await api.get('/api/settings');
      const settings = response.data.data;
      
      const formValues = {
        [SETTING_KEYS.APP_TITLE]: settings.appTitle || '',
        [SETTING_KEYS.DATE_FORMAT]: settings.dateFormat || DATE_FORMATS[0].value,
        [SETTING_KEYS.TIME_FORMAT]: settings.timeFormat || TIME_FORMATS[1].value,
        [SETTING_KEYS.LOGO_URL]: settings.companyLogoUrl || '',
        [SETTING_KEYS.COMPANY_NAME]: settings.companyName || '',
        [SETTING_KEYS.LOGIN_TITLE]: settings.loginPageTitle || ''
      };
      
      form.setFieldsValue(formValues);
      showSuccess('Settings Loaded', 'General settings loaded successfully!');
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      const errorMessage = error.response?.data?.message || 
        error.response?.data?.error || 
        'An unexpected error occurred while fetching settings.';
      showError('Failed to Fetch Settings', errorMessage, 'fetchError', 6);
    } finally {
      hideLoading?.();
      setInitialDataLoading(false);
    }
  }, [form, notificationApi, showError, showLoading, showSuccess]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleLogoUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    const hideLoading = showLoading('uploadLogoLoading', 'Uploading logo...');

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const uploadResponse = await api.post('/api/upload/logo', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
      });

      const imageUrl = uploadResponse.data.url;
      form.setFieldsValue({ [SETTING_KEYS.LOGO_URL]: imageUrl });
      showSuccess('Logo Uploaded', `${file.name} uploaded successfully. Click Save to apply changes.`);
      onSuccess(imageUrl, file);
    } catch (error) {
      console.error('Logo upload failed:', error);
      const errorMessage = error.response?.data?.message || 
        error.response?.data?.error ||
        'Logo upload failed. Please try again.';
      showError('Upload Failed', errorMessage, 'uploadError', 6);
      onError(error);
    } finally {
      hideLoading?.();
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    const hideLoading = showLoading('updateSettingsLoading', 'Saving settings...');

    try {
      // Prepare updates - convert empty strings to NULL for optional fields
      const updates = Object.entries(values).map(([key, value]) => ({
        key,
        value: (key === SETTING_KEYS.COMPANY_NAME || key === SETTING_KEYS.LOGIN_TITLE || key === SETTING_KEYS.LOGO_URL) 
          ? (value === '' ? null : value)
          : value
      }));

      const updateResults = await Promise.allSettled(
        updates.map(({ key, value }) => 
          api.put(`/api/settings/${key}`, { value })
        )
      );

      const failedUpdates = updateResults.filter(result => result.status === 'rejected');
      if (failedUpdates.length > 0) {
        throw failedUpdates[0].reason;
      }

      showSuccess('Settings Updated', 'General settings updated successfully!');
      fetchSettings();
    } catch (error) {
      console.error('Failed to update settings:', error);
      const errorMessage = error.response?.data?.message || 
        error.response?.data?.error ||
        'An unexpected error occurred while saving settings.';
      showError('Failed to Update Settings', errorMessage, 'updateError', 6);
    } finally {
      hideLoading?.();
      setLoading(false);
    }
  };

  if (initialDataLoading) {
    return (
      <div className="general-settings-page">
        <Spin size="large" tip="Loading settings..." 
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }} />
      </div>
    );
  }

  return (
    <div className="general-settings-page">
      <Card
        title={<Title level={4}>General Application Settings</Title>}
        bordered={false}
        className="users-card"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="create-ticket-form"
        >
          <Form.Item
            name={SETTING_KEYS.APP_TITLE}
            label="Application Title"
            rules={[{ required: true, message: 'Please enter an application title!' }]}
          >
            <Input 
              placeholder="e.g., My Company HelpDesk" 
              size="large" 
              className="custom-input" 
            />
          </Form.Item>

          <Form.Item
            name={SETTING_KEYS.COMPANY_NAME}
            label="Company Name"
          >
            <Input 
              placeholder="e.g., Acme Corporation" 
              size="large" 
              className="custom-input" 
              allowClear
            />
          </Form.Item>

          <Form.Item
            name={SETTING_KEYS.LOGIN_TITLE}
            label="Login Page Title"
          >
            <Input 
              placeholder="e.g., Welcome to Our Portal" 
              size="large" 
              className="custom-input" 
              allowClear
            />
          </Form.Item>

          <Form.Item
            name={SETTING_KEYS.DATE_FORMAT}
            label="Date Format"
            rules={[{ required: true, message: 'Please select a date format!' }]}
          >
            <Select 
              placeholder="Select a date format" 
              size="large" 
              className="custom-select"
            >
              {DATE_FORMATS.map(format => (
                <Option key={format.value} value={format.value}>
                  {format.label} (e.g., {format.example})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name={SETTING_KEYS.TIME_FORMAT}
            label="Time Format"
            rules={[{ required: true, message: 'Please select a time format!' }]}
          >
            <Select 
              placeholder="Select a time format" 
              size="large" 
              className="custom-select"
            >
              {TIME_FORMATS.map(format => (
                <Option key={format.value} value={format.value}>
                  {format.label} ({format.example})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name={SETTING_KEYS.LOGO_URL}
            label="Company Logo URL"
          >
            <Input 
              placeholder="Enter image URL or upload" 
              size="large" 
              className="custom-input" 
              allowClear
            />
          </Form.Item>

          <Form.Item label="Upload Company Logo">
            <Upload
              customRequest={handleLogoUpload}
              maxCount={1}
              listType="picture"
              showUploadList={{ showRemoveIcon: true }}
              onRemove={() => {
                const update = {};
                update[SETTING_KEYS.LOGO_URL] = null;
                form.setFieldsValue(update);
              }}
              beforeUpload={(file) => {
                const isImage = ['image/jpeg', 'image/png', 'image/svg+xml'].includes(file.type);
                const isUnder2MB = file.size / 1024 / 1024 < 2;
                
                if (!isImage) {
                  showError('Invalid File Type', 'You can only upload JPG/PNG/SVG files!');
                  return Upload.LIST_IGNORE;
                }
                if (!isUnder2MB) {
                  showError('File Too Large', 'Image must be smaller than 2MB!');
                  return Upload.LIST_IGNORE;
                }
                return true;
              }}
            >
              <Button icon={<UploadOutlined />} size="large">
                Click to Upload
              </Button>
            </Upload>
            {form.getFieldValue(SETTING_KEYS.LOGO_URL) && (
              <div style={{ marginTop: 10 }}>
                <img 
                  src={form.getFieldValue(SETTING_KEYS.LOGO_URL)} 
                  alt="Company Logo Preview" 
                  style={{ 
                    maxWidth: 150, 
                    maxHeight: 50, 
                    border: '1px solid #ddd', 
                    padding: 5, 
                    borderRadius: 4 
                  }} 
                />
              </div>
            )}
          </Form.Item>

          <Form.Item className="submit-button-item">
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                size="large"
                className="create-ticket-submit-button"
              >
                Save Settings
              </Button>
              <Button
                onClick={fetchSettings}
                icon={<ReloadOutlined />}
                size="large"
                className="create-ticket-submit-button"
              >
                Refresh
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default GeneralSettings;